'use client';

import React from 'react';
import Link from 'next/link';

interface CalenddyLogoProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'dark' | 'light' | 'monochrome';
  showText?: boolean;
  className?: string;
  href?: string;
  iconColor?: string;
}

export function CalenddyLogo({
  size = 'md',
  variant = 'dark',
  showText = true,
  className = '',
  href,
  iconColor
}: CalenddyLogoProps) {
  // Dimensions map - icon and wordmark tightly coupled for cohesive brand lockup
  const sizeMap = {
    xs: { icon: 16, text: 'text-xs sm:text-sm', gap: 'gap-0.5' },
    sm: { icon: 26, text: 'text-xl sm:text-2xl', gap: 'gap-0.5' },
    md: { icon: 32, text: 'text-2xl sm:text-3xl', gap: 'gap-1' },
    lg: { icon: 40, text: 'text-3xl sm:text-4xl', gap: 'gap-1' },
    xl: { icon: 48, text: 'text-4xl sm:text-5xl', gap: 'gap-1.5' }
  };

  const { icon, text, gap } = sizeMap[size] || sizeMap.sm;

  // Colors
  const textColor = variant === 'light' ? 'text-white' : 'text-gray-900';

  // Gradient ID per variant
  const gradId = `calenddy-grad-${variant}`;
  const isLight = variant === 'light';

  const logoContent = (
    <div className={`inline-flex items-center ${gap} select-none group ${className}`}>
      {/* Nuevo Isotipo Calenddy: C-Ribbon Concéntrico Fluido (Cohesivo, Moderno y Elegante) */}
      <svg
        width={icon}
        height={icon}
        viewBox="1.2 2 27.2 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0 transition-transform duration-200 group-hover:scale-105"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={isLight ? '#FFFFFF' : '#17A2B8'} />
            <stop offset="100%" stopColor={isLight ? '#E6F6F4' : '#00A896'} />
          </linearGradient>
        </defs>

        {/* Arco exterior continuo formando la C */}
        <path
          d="M25 8.5C22.6 5.7 18.9 4 14.8 4C8.3 4 3 9.4 3 16C3 22.6 8.3 28 14.8 28C21.3 28 26.6 22.7 26.6 16.2"
          stroke={iconColor || `url(#${gradId})`}
          strokeWidth="3.5"
          strokeLinecap="round"
        />

        {/* Arco interior concéntrico fluido (ciclo de turnos y sincronización) */}
        <path
          d="M14.8 10.5C11.8 10.5 9.3 13 9.3 16C9.3 19 11.8 21.5 14.8 21.5C17.8 21.5 20.3 19 20.3 16"
          stroke={iconColor || `url(#${gradId})`}
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>

      {/* Texto Calenddy con la misma tipografía de Noir (font-medium text-gray-900 tracking-tight) */}
      {showText && (
        <span className={`font-medium tracking-tight leading-none ${text} ${textColor}`}>
          Calenddy
        </span>
      )}
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="inline-flex items-center no-underline hover:opacity-90 transition-opacity">
        {logoContent}
      </Link>
    );
  }

  return logoContent;
}
